import { useApolloClient, useQuery } from '@apollo/client/react'
import { createTranslator } from '@athena/i18n'
import {
  AppBottomBar,
  AppFrame,
  AppHeader,
  AppSidebar,
  BottomBarItemContent,
  BrandLockup,
  FOCUS_RING,
  ErrorState,
  HeaderBrand,
  I18nProvider,
  Icon,
  LoadingState,
  Notice,
  PageIdentityProvider,
  ProfileMenu,
  SidebarBrand,
  SidebarItemContent,
  SidebarTooltip,
  ToastProvider,
  bottomBarItemClasses,
  cn,
  headerBrandClasses,
  sidebarBrandClasses,
  sidebarItemClasses,
  useTranslate,
} from '@athena/ui'
import { Link, Outlet, useLocation, useParams } from '@tanstack/react-router'

import { AdminViewerDocument } from '../graphql/generated/operations.js'
import { WEB_APP_URL } from '../lib/apollo.js'
import { signOut, signOutFailed } from '../lib/session.js'
import { useLanguageMenuItems } from './LanguageMenuItems.js'
import { RouteOrientation } from './route-orientation.js'
import { DEFAULT_LANGUAGE, isLanguage, setCurrentLanguage, type Language } from '../lib/language.js'

/**
 * The administration shell, and the gate in front of it.
 *
 * ── The gate is a courtesy ───────────────────────────────────────────────
 *
 * Every operation behind this screen is checked on the server by
 * `requireAdmin`, which is a separate gate from the tutor and student ones
 * rather than a superset of them. What this component does is stop somebody
 * who is not an administrator from looking at a shell full of controls that
 * would all refuse them. A route guard is not a security control, and this one
 * is not doing security work — deleting it would change nothing about who can
 * read whose account.
 *
 * ── Signing in happens in the product ────────────────────────────────────
 *
 * There is no sign-in screen here. An administrator signs in through Athena
 * itself, with the same account and the same three ways in, and arrives with a
 * session. Reimplementing that flow would be a second implementation of the
 * one part of the product that must not have two — and it would need the
 * provider list, the code form, the rate limiting and the cookie handling all
 * over again.
 */
export function AdminLayout() {
  const { language } = useParams({ from: '/$language' })
  const resolved: Language = isLanguage(language) ? language : DEFAULT_LANGUAGE

  // Above every component that can start a query, so a request carries the
  // language of the page that made it — the same arrangement the product uses.
  setCurrentLanguage(resolved)

  return (
    <I18nProvider language={resolved}>
      {/* "Accounts · Athena Administration": every screen names itself, and
          the access gate is the suffix alone. */}
      <PageIdentityProvider suffix={titleSuffix(resolved)}>
        {/* The one toast host, above every admin screen's outlet, so a
            confirmation raised before a navigation survives it. */}
        <ToastProvider>
          <RouteOrientation />
          <AdminShell language={resolved} />
        </ToastProvider>
      </PageIdentityProvider>
    </I18nProvider>
  )
}

function AdminShell({ language }: { language: Language }) {
  const t = useTranslate()
  const { data, loading, error } = useQuery(AdminViewerDocument)
  const pathname = useLocation({ select: (location) => location.pathname }).replace(/\/$/, '')

  if (loading) return <LoadingState>{t('common.loading')}</LoadingState>
  if (error) return <ErrorState>{t('appError.unreachableBody')}</ErrorState>

  const viewer = data?.me

  /**
   * Signed out, or signed in as somebody who is not an administrator.
   *
   * One screen for both, deliberately. Telling the two apart would tell an
   * ordinary tutor who found this URL that administrator accounts exist and
   * that theirs is not one — which is an answer nobody here needs to give.
   */
  if (!viewer?.admin) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center gap-4 p-8">
        <BrandLockup />
        {/* The application's name, and nothing about why the reader is not
            in it: the same heading for signed-out and for not-an-admin, like
            the rest of this screen. */}
        <h1 tabIndex={-1} className="sr-only">
          {t('admin.titleSuffix')}
        </h1>
        <Notice title={t('admin.noAccessTitle')}>{t('admin.noAccessBody')}</Notice>
        <a
          href={WEB_APP_URL}
          className={cn('rounded-sm text-body font-semibold text-primary underline', FOCUS_RING)}
        >
          {t('admin.openAthena')}
        </a>
      </main>
    )
  }

  return <SignedInShell language={language} pathname={pathname} viewer={viewer} />
}

/**
 * The frame the product uses — the same sidebar, rail and bottom bar, from the
 * same parts — with the administration's own contents: denser destinations,
 * a header that says where you are rather than holding a search, and the
 * shared account menu for language and signing out.
 */
function SignedInShell({
  language,
  pathname,
  viewer,
}: {
  language: Language
  pathname: string
  viewer: {
    email: string
    avatarUrl?: string | null | undefined
    admin: { displayName: string | null } | null
  }
}) {
  const t = useTranslate()
  const client = useApolloClient()
  const languageItems = useLanguageMenuItems()
  const signOutDidNotFinish = useLocation({
    select: (location) => signOutFailed(location.searchStr),
  })
  const prefix = `/${language}`
  /*
   * Icons that say what each place holds. Requests is an inbox — things
   * waiting for a decision — where it used to be the "info" glyph; Subjects is
   * a library where it used to borrow the practices' book.
   */
  const nav = [
    {
      id: 'requests',
      to: '/$language/requests' as const,
      label: t('admin.navRequests'),
      icon: <Icon.Inbox />,
    },
    { id: 'accounts', to: '/$language' as const, label: t('admin.navUsers'), icon: <Icon.Users /> },
    {
      id: 'subjects',
      to: '/$language/subjects' as const,
      label: t('management.subjects'),
      icon: <Icon.Library />,
    },
    {
      id: 'ai',
      to: '/$language/ai' as const,
      label: t('aiAnalytics.nav'),
      icon: <Icon.TrendingUp />,
    },
  ]
  const active = (id: string) =>
    id === 'accounts'
      ? pathname === prefix ||
        pathname.startsWith(`${prefix}/accounts/`) ||
        pathname.startsWith(`${prefix}/users/`)
      : pathname === `${prefix}/${id}` || pathname.startsWith(`${prefix}/${id}/`)
  const here = nav.find((entry) => active(entry.id))

  return (
    <AppFrame
      sidebar={
        <AppSidebar label={t('admin.navLabel')}>
          <Link
            to="/$language"
            params={{ language }}
            activeOptions={{ exact: true }}
            className={sidebarBrandClasses}
          >
            <SidebarBrand label={t('navigation.brandHome')} />
          </Link>
          {nav.map((entry) => (
            <SidebarTooltip key={entry.id} label={entry.label}>
              <Link
                to={entry.to}
                params={{ language }}
                activeOptions={{ exact: true }}
                aria-current={active(entry.id) ? 'page' : undefined}
                className={sidebarItemClasses(active(entry.id), 'compact')}
              >
                <SidebarItemContent icon={entry.icon} label={entry.label} />
              </Link>
            </SidebarTooltip>
          ))}
        </AppSidebar>
      }
      bottomBar={
        <AppBottomBar label={t('admin.navLabel')}>
          {nav.map((entry) => (
            <Link
              key={entry.id}
              to={entry.to}
              params={{ language }}
              activeOptions={{ exact: true }}
              aria-current={active(entry.id) ? 'page' : undefined}
              className={bottomBarItemClasses(active(entry.id))}
            >
              <BottomBarItemContent icon={entry.icon} label={entry.label} />
            </Link>
          ))}
        </AppBottomBar>
      }
      mainClassName="mx-auto flex w-full max-w-6xl min-w-0 flex-col gap-6 px-4 py-6 md:px-6 md:pb-8 desktop:px-8"
      header={
        <AppHeader className="flex h-header items-center gap-3">
          <Link
            to="/$language"
            params={{ language }}
            activeOptions={{ exact: true }}
            className={headerBrandClasses}
          >
            <HeaderBrand label={t('navigation.brandHome')} />
          </Link>
          {/* Where you are: the application, then the part of it. Not a
            heading — each screen brings the page's one h1 — and not a second
            copy of that h1 either: on an account's page this says "Accounts"
            and the page says whose. */}
          <p className="mr-auto flex min-w-0 items-baseline gap-2 text-body-sm">
            <span className="shrink-0 font-semibold text-text">{t('admin.badge')}</span>
            {here ? (
              <>
                <span aria-hidden="true" className="text-text-muted">
                  /
                </span>
                <span className="truncate text-text-secondary">{here.label}</span>
              </>
            ) : null}
          </p>
          <ProfileMenu
            name={viewer.admin?.displayName || viewer.email}
            email={viewer.email}
            avatarUrl={viewer.avatarUrl}
            groups={[{ id: 'language', label: t('language.menuLabel') }]}
            items={[
              ...languageItems,
              {
                id: 'sign-out',
                label: t('navigation.signOut'),
                onSelect: () => void signOut(client),
              },
            ]}
          />
        </AppHeader>
      }
    >
      {/* After a sign-out the server did not confirm: the session may still be
          alive, and on a shared machine that is the one thing the person
          leaving has to know (SECURITY_AUDIT.md SEC-005). */}
      {signOutDidNotFinish ? (
        <Notice tone="error" title={t('admin.signOutFailedTitle')} className="m-4">
          {t('admin.signOutFailedBody')}
        </Notice>
      ) : null}
      <Outlet />
    </AppFrame>
  )
}

/** The admin application's own name, after every page's: "Accounts · Athena Administration". */
function titleSuffix(language: Language): string {
  return createTranslator(language)('admin.titleSuffix')
}
